import { Dto } from '@damofire/data-commons';
import { ProductColor } from '@damofire/data-repos';
import { ModelItem, ProductionMaterial, RegroupedItem, Supplier } from '..';
import { BraceMeasurements } from './brace-drawing.dto';
import { DeflexMeasurements } from './deflex-drawing.dto';
import { HookMeasurements } from './hook-drawing.dto';
import { Base, UnitMeasurements } from './unit-drawing.dto';

export interface ProductionDrawingParams {
  productionItemId?: number;
  productType?: ProductionDrawingProductType;
  drawingParameters?: ProductionDrawingParameters;
  createdBy?: string;
  lastModifiedBy?: string;
  processed?: boolean;
  filename?: string;
  new?: boolean;
  overwriteCnt?: number;
  createdDate?: string;
  lastModifiedDate?: string;
  quantity?: number;
  isSupplierDrawing?: boolean;
  supplier?: Supplier;
}

export type Measurements = UnitMeasurements | HookMeasurements | DeflexMeasurements | BraceMeasurements;

export interface ProductionDrawingParameters {
  jobNumber?: string;
  salesOrderNumber?: string;
  quoteNumber?: string;
  drawingNotes?: string;
  customerName?: string;
  distributorName?: string;
  model?: string;
  location?: string;
  color?: ProductColor;
  quantity?: number;
  measurements?: Measurements;
  items?: ProductionMaterial[];
  filename?: string;
  name?: string;
  modelItems?: ModelItem[];
  regroupedItems?: RegroupedItem;
  unitBase?: Base;
}

export interface CuttingItem extends Dto {
  angle?: number;
  customer?: string;
  description?: string;
  distributor?: string;
  drawing?: string;
  front?: boolean;
  group?: string;
  height?: number;
  inventoryCode?: string;
  job?: string;
  length?: number;
  location?: string;
  manufacturer?: string;
  model?: string;
  perforated?: boolean;
  product?: string;
  productionItemId?: number;
  quantity?: number;
  thick?: number;
  unit?: string;
  unitType?: string;
  width?: number;
}

export interface ProductionDrawing extends Dto<string> {
  archived?: boolean;
  name?: string;
  path?: string;
  divisionId?: string;
  organizationId?: string;
  params?: ProductionDrawingParams;
}

export enum ProductionDrawingFileType {
  DXF = '.DXF',
  SVG = '.SVG',
}

export function unitType(drawingName: string): string {
  const drawingPrefix = drawingName.split(/^([a-zA-Z0-9]+)([_-]).+/)[1];
  let drawingType: string;

  const allTypes: string[] = ['HO', 'AB', 'DFX', 'FE', 'HARDWARE'];
  drawingType = allTypes.find((t) => t === drawingPrefix);
  if (!drawingType) {
    drawingType = 'UNIT';
  }

  return drawingType;
}

export enum ProductionDrawingProductType {
  UNIT = 'UNIT',
  HOOK_OVER = 'HOOK_OVER',
  BRACE = 'BRACE',
  PLATE = 'DEFLEX',
  HARDWARE = 'HARDWARE',
  FENCE = 'FENCE',
  END_GUARD = 'END_GUARD',
  SUPPLIER_MODEL = 'SUPPLIER_MODEL',
}

export enum RackSupplier {
  ACPAC = 'ACPAC',
  ARCAN = 'ARCAN',
  ARCO_TUBE = 'ARCO_TUBE',
  BEAM_LOCK = 'BEAM_LOCK',
  CENTENIAL = 'CENTENIAL',
  CUBIC = 'CUBIC',
  CUBIC_DOUBLE = 'CUBIC_DOUBLE',
  CUBIC_TRIPLE = 'CUBIC_TRIPLE',
  FRASER_STRUCTURAL = 'FRASER_STRUCTURAL',
  HILINE = 'HILINE',
  INTERLAKE = 'INTERLAKE',
  KINGWAY = 'KINGWAY',
  KINGWAY_DOUBLE = 'KINGWAY_DOUBLE',
  MECALUX = 'MECALUX',
  MASTORACK = 'MASTORACK',
  MASTORACK_DOUBLE = 'MASTORACK_DOUBLE',
  MASTORACK_HD = 'MASTORACK_HD',
  MASTORACK_HD_DOUBLE = 'MASTORACK_HD_DOUBLE',
  MAXIE_RACK = 'MAXIE_RACK',
  NAS = 'NAS',
  PACIFIC = 'PACIFIC',
  PALMER_SHILE = 'PALMER_SHILE',
  PEDLER = 'PEDLER',
  PENCO = 'PENCO',
  PRESS_STEEL = 'PRESS_STEEL',
  RAPID_RACK = 'RAPID_RACK',
  REDI_RACK = 'REDI_RACK',
  REPUBLIC = 'REPUBLIC',
  RIDG_U_RACK = 'RIDG_U_RACK',
  SAMMONS = 'SAMMONS',
  SPACE_AID = 'SPACE_AID',
  SPACERACK = 'SPACERACK',
  SPEED_RACK = 'SPEED_RACK',
  SPEED_RACK_T = 'SPEED_RACK_T',
  STEEL_KING = 'STEEL_KING',
  STURDY_BUILD = 'STURDY_BUILD',
  SUPER_RACK = 'SUPER_RACK',
  TEAR_DROP = 'TEAR_DROP',
  TRIPLE_A = 'TRIPLE_A',
  UNARCO = 'UNARCO',
  USP = 'USP',
  WEB = 'WEB',
}

export interface DrawingGenerationResponse {
  status: string;
  message?: string;
  params?: ProductionDrawingParameters;
}

export interface GenerationParameters extends ProductionDrawingParameters {
  drawingType?: ProductionDrawingProductType;
}
